import { useMutation } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import {
  DemoError,
  demoPageUrl,
  runDemo,
  type DemoIssue,
  type DemoOrientation,
  type DemoProvider,
  type DemoRunResult,
  type DemoValue,
  type EdgeMoveInput,
} from "@/api";
import { Button } from "@/components/Button";
import { DEMO_CASES, type DemoCase } from "@/components/demoCases";
import { DrawingPane } from "@/components/DrawingPane";
import { OutlineReview } from "@/components/OutlineReview";
import { Panel } from "@/components/Panel";
import { PartsList } from "@/components/PartsList";
import { RunSteps, useRunClock, type RunPhase } from "@/components/RunSteps";
import { Select } from "@/components/fields/Select";
import { StateMessage } from "@/components/StateMessage";
import { StatusBadge } from "@/components/StatusBadge";
import { sourceStateOf, ValueList, type ValuePlacement } from "@/components/ValueList";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { useSession } from "@/store/session";

import styles from "./DemoScreen.module.css";

/**
 * One real drawing, run through the whole chain, in front of an audience.
 *
 * Almost everything on this screen only shows: no editing, no saving, no
 * confirming of individual values — a button that looks like it works is
 * remembered as a promise (spec §1, "gezeigt heißt zugesagt"), and confirming
 * values is A-014's own step, arriving with E3. The one exception is the
 * reconstructed outline: a person can say it is the same house, drawn right —
 * and that confirmation lives only in this browser tab. Nothing about it is
 * saved.
 */

/**
 * Shown near the run header whenever the answer on screen says a person
 * typed it in by hand — never guessed from the case id: a case re-recorded
 * from a live model run must stop reading as hand-entered the moment its
 * record does (review finding, 11.08.2026; see `DemoRunResult.recordedProvider`).
 *
 * ST-3 and A-6 (`DEMO_CASES` in `@/components/demoCases`) are rejected by the
 * semantic stage, live and historically, so their recorded runs are a
 * hand-curated reading rather than a model's own answer (design spec §5) —
 * this note says so whenever the run on screen is one, whichever case
 * produced it.
 */
const CURATED_NOTE = "Lesung vom Bearbeiter erfasst — nicht vom Modell gelesen";

const ERROR_TITLES: Record<string, string> = {
  demo_unreachable: "Der Demo-Dienst antwortet nicht.",
  provider_unavailable: "Der Anbieter hat nicht geantwortet.",
  provider_unusable: "Die Antwort des Anbieters ist unbrauchbar.",
  release_missing: "Die Freigabe ist nicht scharfgeschaltet.",
  endpoint_not_permitted: "Der Endpunkt ist nicht zulässig.",
  case_sealed: "Der Fall ist gesperrt.",
  case_unknown: "Der Fall ist nicht im Register.",
  case_not_runnable: "Zu diesem Fall liegt keine Datei vor.",
  case_files_unavailable: "Die Zeichnung ist auf diesem Rechner nicht erreichbar.",
  replay_missing: "Es gibt keinen aufgezeichneten Lauf.",
  replay_unusable: "Der aufgezeichnete Lauf ist unbrauchbar.",
  outline_unknown: "Diese Form gibt es im neuen Lauf nicht mehr.",
  // The three ways a correction can be refused. They arrive from `demo.py` as
  // named states (spec §5) and were unreachable until a wall could be dragged;
  // an unnamed one would show "Der Lauf ist abgebrochen." over a drawing
  // somebody had just touched, which says nothing about what they did.
  edit_not_draggable: "Diese Wand lässt sich nicht verschieben.",
  edit_segment_unknown: "Diese Wand gibt es im neuen Lauf nicht mehr.",
  outline_required: "Zur Korrektur fehlt die Formauswahl.",
};

const ERROR_ADVICE: Record<string, string> = {
  demo_unreachable: "Der Dienst läuft im selben Prozess wie die App, unter „/demo“.",
  provider_unavailable: "Der aufgezeichnete Lauf zeigt dieselbe Kette aus einem Protokoll.",
  release_missing: "Ohne bestätigte Freigabe überträgt der Adapter nichts. Das ist so gewollt.",
  endpoint_not_permitted:
    "Die EU-Zusage gilt nur für den Standard-Endpunkt. Der Lauf bricht vor der Übertragung ab.",
  case_sealed: "A-4 und A-7 bleiben bis zum Einfrieren des Prompts unangetastet.",
  // Does not send the reader to a list that is not there: after this failure
  // the candidates on screen are the ones from the run before, and the advice
  // used to say "deshalb wählt man unten aus der neuen Liste" (review finding
  // N-1, 10.08.2026). There is no new list until a new run has produced one.
  outline_unknown:
    "Die Formauswahl startet den Lauf neu, und der Anbieter gruppiert die Teilzüge dabei nicht " +
    "immer gleich — vier Läufe derselben Zeichnung ergaben zwei bis drei. Die Formen werden aus " +
    "den neuen Teilzügen gerechnet; die Liste weiter unten ist noch die des vorherigen Laufs. " +
    "Erst ein neuer Lauf bringt Formen, aus denen sich wählen lässt.",
  edit_not_draggable:
    "Eine Wand wird senkrecht zu sich selbst geschoben, und die Nachbarwände geben die Länge " +
    "dafür her. Wo eine Nachbarwand in dieselbe Richtung läuft — ein gerader Stoß — hat sie " +
    "nichts herzugeben, und die Form würde beim Schieben kaputtgehen. Der Lauf startet ohne " +
    "diese Korrektur neu; eine andere Wand desselben Umrisses lässt sich weiterhin schieben.",
  edit_segment_unknown:
    "Die Wände werden je Lauf neu durchnummeriert, und der Anbieter gruppiert die Teilzüge " +
    "nicht immer gleich. Eine Korrektur aus einem früheren Lauf zeigt dann auf eine Wand, die " +
    "es so nicht mehr gibt.",
};

/**
 * Why the result on screen is not the current one — for the two controls that
 * would otherwise act on it.
 *
 * A failed run leaves the previous result standing (see `shown`), and with it
 * a shape picker whose ids the backend may no longer know and a confirm button
 * that would write "Bestätigt von …" under a red error card (review findings
 * N-1/N-8, 10.08.2026). The run in flight is covered by `busy` instead: that
 * one is going to answer.
 */
const SUPERSEDED_NOTE =
  "Abgebrochener Lauf — diese Formen stammen aus dem Lauf davor. Erst ein neuer Lauf bringt " +
  "eine Form, die sich wählen und bestätigen lässt.";

/**
 * Why the live run is not offered for the case in the picker.
 *
 * A case without a drawing behind it (`liveRunnable` in `@/components/demoCases`)
 * answers "Lauf starten" with HTTP 409 `case_not_runnable` — so the most
 * prominent button on the screen was the one that failed, in front of an
 * audience. The button is disabled rather than hidden: this screen already
 * disables what is momentarily out of reach (both header buttons while a run
 * is in flight, the case picker with them), and a control that disappears
 * makes a person look for it instead of reading why it is off.
 */
const NO_LIVE_RUN_NOTE =
  "Zu diesem Fall liegt keine Zeichnung vor — ein Live-Lauf braucht sie. " +
  "Der aufgezeichnete Lauf zeigt dieselbe Kette.";

/**
 * Error classes for which repeating the *same* request repeats the failure.
 *
 * `outline_unknown` is the whole list: the id came from an earlier run's
 * candidates and cannot come back, so "Erneut versuchen" with it attached
 * walks into the same 422 (review finding C2, 10.08.2026). The retry drops
 * the shape and says so in its own label instead of quietly changing what it
 * asks for.
 */
const RETRY_WITHOUT_OUTLINE = new Set(["outline_unknown"]);

/**
 * Error classes for which the *corrections* are what repeating the request
 * would repeat.
 *
 * The same rule as `RETRY_WITHOUT_OUTLINE` one field over, and it became
 * reachable with the drag: a wall whose neighbour runs parallel to it can
 * never be moved (`apply_edits` refuses a straight joint outright), and an
 * index from an earlier run may not name a wall in this one. Retrying with
 * that correction still attached walks into the same 422 for ever. So the
 * retry drops it — and the button says which, rather than quietly changing
 * what it asks for (review finding C2, 10.08.2026).
 */
const RETRY_WITHOUT_EDITS = new Set([
  "edit_not_draggable",
  "edit_segment_unknown",
  "outline_required",
]);

const ORIENTATION_OUTCOMES: Record<string, string> = {
  not_run: "nicht geprüft",
  dominant: "eindeutig",
  tied: "zwei Lagen gleichauf",
  ambiguous: "mehrdeutig",
  no_signal: "kein Anhaltspunkt auf dem Blatt",
  sweep_failed: "Prüfung fehlgeschlagen",
  mixed: "Seiten unterschiedlich gedreht",
};

const SEVERITY_STATUS: Record<string, "conflict" | "review"> = {
  reject_run: "conflict",
  drop_value: "conflict",
  degrade: "review",
};

function OrientationBand({ orientation }: { orientation: DemoOrientation }) {
  const degrees = orientation.appliedDegrees;
  const headline = !orientation.certain
    ? "Lage unsicher — nicht geprüft"
    : degrees === null
      ? "Seiten unterschiedlich gedreht"
      : degrees === 0
        ? "Blatt aufrecht erkannt"
        : `Blatt um ${degrees}° gedreht erkannt`;

  return (
    <div className={styles.orientation}>
      <StatusBadge status={orientation.certain ? "auto" : "review"}>
        {orientation.certain ? "Lage erkannt" : "Lage unsicher"}
      </StatusBadge>
      <p className={styles.orientationText}>
        <strong>{headline}.</strong>{" "}
        {ORIENTATION_OUTCOMES[orientation.outcome] ?? orientation.outcome}
        {degrees !== null && degrees > 0 ? (
          <>
            {" "}
            · gelesen wurde die um <span className="num">{degrees}°</span> gedrehte Seite
          </>
        ) : null}
      </p>
    </div>
  );
}

function IssueList({ issues }: { issues: DemoIssue[] }) {
  // Grouped by rule, with a count. Eleven identical sentences are not eleven
  // findings on screen, they are one finding eleven times — and the count is
  // the part that matters.
  const grouped = useMemo(() => {
    const byRule = new Map<string, { issue: DemoIssue; count: number }>();
    for (const issue of issues) {
      const key = `${issue.source}:${issue.rule}`;
      const seen = byRule.get(key);
      if (seen) seen.count += 1;
      else byRule.set(key, { issue, count: 1 });
    }
    return [...byRule.values()];
  }, [issues]);

  if (grouped.length === 0) {
    return (
      <StateMessage title="Keine Hinweise">
        Die Prüfschicht hat an diesem Lauf nichts zu beanstanden.
      </StateMessage>
    );
  }

  return (
    <ul className={styles.issues}>
      {grouped.map(({ issue, count }) => (
        <li key={`${issue.source}:${issue.rule}`} className={styles.issue}>
          <span className={styles.issueHead}>
            <StatusBadge status={SEVERITY_STATUS[issue.severity] ?? "neutral"}>
              {issue.severity}
            </StatusBadge>
            <span className={`num ${styles.rule}`}>{issue.rule}</span>
            <span className={`label ${styles.issueSource}`}>{issue.source}</span>
            {count > 1 ? (
              <span className={`num ${styles.issueCount}`}>{count}×</span>
            ) : null}
          </span>
          <p className={styles.issueText}>{issue.message}</p>
        </li>
      ))}
    </ul>
  );
}

interface RunVariables {
  caseId: string;
  provider: DemoProvider;
  /** Which arrangement to draw — set once a person picks a shape, or reruns one. */
  outline?: string;
  /**
   * Every correction accumulated for this run, complete and verbatim (spec
   * §6.2). This screen's record of "the edits" is `run.variables?.edits` —
   * the same place it already keeps the current provider and outline
   * (`startedProvider`, `retryOutline` below) — rather than a second,
   * parallel piece of state that could drift from what was actually sent.
   * Task 8 reads it the same way to build the next drag's request.
   */
  edits: EdgeMoveInput[];
}

/** What to run when nothing has been picked yet — first in the demo order. */
const INITIAL_CASE: DemoCase = DEMO_CASES[0]!;

export function DemoScreen() {
  useDocumentTitle("Durchstich");

  const [caseId, setCaseId] = useState<string>(INITIAL_CASE.id);
  const [markedId, setMarkedId] = useState<string | null>(null);
  const [confirmedOutlineId, setConfirmedOutlineId] = useState<string | null>(null);
  /**
   * The last run that came back, held across the next one.
   *
   * `useMutation` drops `data` the moment `mutate` is called again, and
   * picking a shape *is* a new run — so a click emptied the whole screen for
   * about a minute: outline, readings and parts list all gone, and nothing to
   * compare the next shape against (review finding I6, 10.08.2026). Keeping
   * the previous result is only honest with the receipt line saying it is the
   * previous one, which `stale` below does.
   */
  const [shown, setShown] = useState<DemoRunResult | null>(null);
  const reviewer = useSession((state) => state.reviewer);

  const run = useMutation<DemoRunResult, Error, RunVariables>({
    mutationFn: ({ caseId, provider, outline, edits }) =>
      runDemo({ caseId, provider, outline, edits }),
    onSuccess: (data) => setShown(data),
  });

  const [elapsed, startClock] = useRunClock(run.isPending);

  /**
   * `edits` defaults to `[]` — a fresh start, from either header button, a
   * retry that drops the dead shape, or a freshly chosen case. The two calls
   * that mean to carry corrections forward (retry, and Task 8's eventual
   * re-run after a drag) pass them explicitly.
   */
  function startRun(
    provider: DemoProvider,
    options: { caseId?: string; outline?: string; edits?: EdgeMoveInput[] } = {},
  ) {
    const nextEdits = options.edits ?? [];
    setMarkedId(null);
    setConfirmedOutlineId(null);
    startClock();
    run.mutate({
      caseId: options.caseId ?? caseId,
      provider,
      outline: options.outline,
      edits: nextEdits,
    });
  }

  function chooseCase(nextCaseId: string) {
    setCaseId(nextCaseId);
    // "replay", not the previous provider: switching between nine cases has
    // to stay safe to click through, and a live model call belongs behind
    // the explicit "Lauf starten" button, never behind a picker.
    startRun("replay", { caseId: nextCaseId });
  }

  function chooseOutline(outlineId: string) {
    // OutlineReview only appears once a run has produced candidates, so a
    // provider from that same run is always on hand — "replay" here only
    // satisfies the type and is not a branch this can really reach.
    startRun(run.variables?.provider ?? "replay", { outline: outlineId });
  }

  function confirmOutline(outlineId: string) {
    setConfirmedOutlineId(outlineId);
  }

  const result = shown;
  /**
   * The shape currently drawn — the one a correction is made *to*.
   *
   * Read off the answer rather than off `run.variables?.outline`, which is
   * only what the last request *asked* for and stays put even when that
   * request has just failed — a dead `outline_unknown` id, for instance.
   * `result` survives a failed run (see `shown`), and reading the answer is
   * the only way to see the second way a shape gets drawn: since 12.08.2026
   * the backend may settle an unambiguous reading itself and plan it
   * unasked (`chosenBy: "system"`, design spec §2), and that shape carries
   * `chosen` exactly like one somebody clicked. A correction lands on either
   * — `apply_edits` does not care who picked. What stays untouched is A-014:
   * a shape the system drew is a proposal, never confirmed without a person,
   * and confirming is still this screen's own separate act. `drawnOutlineId`
   * is `undefined` only where neither happened, and a correction then has no
   * shape to name (`outline_required`).
   */
  const drawnOutlineId = result?.outlines.find((shape) => shape.chosen)?.id;

  /**
   * Somebody moved a wall (or took the corrections back). The whole list
   * arrives, never a delta — this process holds no session, so the request is
   * the record (spec §6.2).
   *
   * It is a full re-run, not a patch: the corrected shape is the shape the
   * reconciliation, the bracket count and the parts list are all about, and
   * `demo.py` recomputes them together. A confirmation from before the
   * correction falls away with it, which `startRun` already does — the form
   * somebody signed off is not the form on screen any more.
   */
  function adjustOutline(nextEdits: EdgeMoveInput[]) {
    startRun(run.variables?.provider ?? "replay", {
      outline: drawnOutlineId,
      edits: nextEdits,
    });
  }
  /** On screen, but not from the attempt that is running or has just failed. */
  const stale = result !== null && (run.isPending || run.isError);

  const phase: RunPhase = run.isPending
    ? "running"
    : run.isError
      ? "failed"
      : result
        ? "done"
        : "idle";

  const marked: DemoValue | null = useMemo(
    () => result?.values.find((value) => value.id === markedId) ?? null,
    [result, markedId],
  );

  /** Reading → where it was placed and what the drawing said about goods there. */
  const placement = useMemo(() => {
    const map: ValuePlacement = new Map();
    for (const partial of result?.runs ?? []) {
      for (const edge of partial.edges) {
        if (edge.valueId) {
          map.set(edge.valueId, {
            runLabel: partial.label,
            gratingPresence: edge.gratingPresence,
          });
        }
      }
    }
    return map;
  }, [result]);

  const error = run.error instanceof DemoError ? run.error : null;
  const errorClass = error?.errorClass ?? (run.isError ? "unknown_error" : null);
  const startedProvider = run.variables?.provider;
  // Carried into retry below: a run that failed right after somebody chose a
  // shape must retry *that* shape, or "Erneut versuchen" would silently fall
  // back to the unchosen arrangement and show a different result without
  // saying the choice was dropped (review finding, 10.08.2026).
  //
  // Except where the shape is what failed: an `outline_unknown` id does not
  // exist any more, so retrying with it repeats the same 422. There the run
  // starts without a shape, and the button below says that rather than
  // dropping it silently — the same rule as above, applied in the other
  // direction (review finding C2, 10.08.2026).
  const dropOutline = RETRY_WITHOUT_OUTLINE.has(errorClass ?? "");
  const retryOutline = dropOutline ? undefined : run.variables?.outline;
  // Same rule for the corrections: a drag the shape cannot carry fails the
  // same way every time it is sent (see `RETRY_WITHOUT_EDITS`).
  const dropEdits = RETRY_WITHOUT_EDITS.has(errorClass ?? "");
  const retryEdits = dropEdits ? [] : run.variables?.edits;
  const confirmedBy = confirmedOutlineId ? reviewer : null;
  // Unknown ids default to runnable: the picker only offers ids from this
  // list, and refusing a run for a case nobody could have chosen would hide
  // the register's own error message behind a greyed-out button.
  const liveRunnable =
    DEMO_CASES.find((demoCase) => demoCase.id === caseId)?.liveRunnable ?? true;

  return (
    <div className={styles.screen}>
      <header className={styles.head}>
        <div>
          {/* The kicker over the screen's title carries its one red mesh
              marker, as a screen head's does (Task 11, UX-Gutachten 1, B-11). */}
          <p className={`label ${styles.kicker}`}>
            <span className="marke marke-titel" aria-hidden="true" />
            Durchstich · Fall {caseId}
          </p>
          <h1 className={styles.title}>{result?.title ?? "Handskizze bis Stückliste"}</h1>
          <p className={styles.lede}>
            Eine echte Kundenzeichnung läuft durch die vollständige Kette und endet in einer
            Stückliste. Nichts davon ist gespeichert — auch die Bestätigung der Form verlässt
            diesen Bildschirm nicht.
          </p>
          <label className={styles.casePicker}>
            <span className="label">Fall</span>
            <Select
              className={styles.caseSelect}
              value={caseId}
              onChange={(event) => chooseCase(event.target.value)}
              disabled={run.isPending}
            >
              {DEMO_CASES.map((demoCase) => (
                <option key={demoCase.id} value={demoCase.id}>
                  {demoCase.id} · {demoCase.label}
                </option>
              ))}
            </Select>
          </label>
          {result?.recordedProvider === "user_entered" ? (
            <p className={styles.caseNote}>
              <StatusBadge status="review">Manuell erfasst</StatusBadge>
              <span>{CURATED_NOTE}</span>
            </p>
          ) : null}
        </div>
        <div className={styles.headActions}>
          <Button
            variant="primary"
            onClick={() => startRun("mistral")}
            busy={run.isPending}
            disabled={run.isPending || !liveRunnable}
            title={liveRunnable ? undefined : NO_LIVE_RUN_NOTE}
            aria-description={liveRunnable ? undefined : NO_LIVE_RUN_NOTE}
          >
            {run.isPending ? "Läuft …" : result ? "Erneut laufen" : "Lauf starten"}
          </Button>
          {/*
            The recorded run is reachable on purpose, not only after a failure.
            Two live runs of the same drawing do not return the same reading:
            the model groups the sections differently each time, so the parts
            list moves and a reading can drop out of it altogether. A recorded
            run is the one that can be talked through. Which one is on screen
            is stated above the result, so nothing is passed off as live.
          */}
          <Button onClick={() => startRun("replay")} disabled={run.isPending}>
            Aufgezeichneten Lauf zeigen
          </Button>
        </div>
      </header>

      <RunSteps phase={phase} elapsedMs={elapsed} timings={result?.timings} />

      {run.isError ? (
        <StateMessage
          tone="conflict"
          title={ERROR_TITLES[errorClass ?? ""] ?? "Der Lauf ist abgebrochen."}
          // No recorded-run button here any more — it sits in the header and is
          // reachable at any time. Repeating it would give the same action two
          // places to be, and a failure is not the only reason to reach for it.
          action={
            <div className={styles.errorActions}>
              {/* Ink, not red: the header's „Erneut laufen" stays the one red
                  main action of this screen (design 2026-10-24 §8.2). */}
              <Button
                variant="tinte"
                onClick={() =>
                  // Corrections retry along with the shape: a retry is the
                  // same attempt again, not a reason to forget what somebody
                  // had already dragged (same rule `retryOutline` follows
                  // for the shape itself, just above).
                  startRun(startedProvider ?? "mistral", {
                    outline: retryOutline,
                    edits: retryEdits,
                  })
                }
              >
                {dropOutline
                  ? "Ohne Formauswahl neu laufen"
                  : dropEdits
                    ? "Ohne die Korrektur neu laufen"
                    : "Erneut versuchen"}
              </Button>
            </div>
          }
        >
          <span className={styles.errorBody}>
            {ERROR_ADVICE[errorClass ?? ""] ? <span>{ERROR_ADVICE[errorClass ?? ""]} </span> : null}
            <span className={`num ${styles.errorClass}`}>{errorClass}</span>
            <span className={styles.errorMessage}>{run.error?.message}</span>
          </span>
        </StateMessage>
      ) : null}

      {/* A class other than "valid" ends the run with nothing to show: the
          prompt forbids a rejected or special-case document from carrying
          measurements, so none are read and no contour is asked for. Said
          plainly here, because an empty screen otherwise reads as a broken
          app rather than as a judgement somebody may want to disagree with. */}
      {result && result.documentClass && result.documentClass !== "valid" ? (
        <StateMessage
          tone="conflict"
          title={
            result.documentClass === "reject"
              ? "Das Modell hat dieses Blatt abgewiesen."
              : "Das Modell hat dieses Blatt als Sonderfall eingestuft."
          }
        >
          <span>
            {result.classReason
              ? result.classReason
              : "Es hat keinen Grund angegeben."}{" "}
            Ein abgewiesenes Blatt trägt laut Prompt keine Maße — deshalb steht unten
            nichts. Bei Uneinigkeit hilft ein zweiter Lauf: die Einstufung ist eine
            Ermessensfrage und fällt nicht jedes Mal gleich aus.
          </span>
        </StateMessage>
      ) : null}

      {result ? (
        <>
          <div className={styles.receipt}>
            {/* The result outlives the run that produced it (see `shown`), so
                it has to say when it is no longer the current one. A figure
                without its origin is the thing this screen exists not to
                show. */}
            {stale ? (
              <span className={`${styles.receiptItem} ${styles.stale}`}>
                {run.isPending
                  ? "Vorheriger Lauf — der neue läuft noch"
                  : "Vorheriger Lauf — der neue ist abgebrochen"}
              </span>
            ) : null}
            <span className={styles.receiptItem}>
              {result.provider === "replay" ? "Aufgezeichneter Lauf" : "Live gelesen"}
            </span>
            <span className={`num ${styles.receiptItem}`}>{result.promptId}</span>
            <span className={`num ${styles.receiptItem}`}>{result.ocrModel}</span>
            <span className={`num ${styles.receiptItem}`}>{result.semanticModel}</span>
            {/* The contour can come from a different provider than the two
                above (`contourProvider`). Named separately because it is a
                different transfer to a different company — and carrying its
                own reason, because "abstracted sheet" is what permits it. */}
            {result.contourModel ? (
              <span
                className={`num ${styles.receiptItem}`}
                title={result.contourBasis ?? undefined}
              >
                Kontur: {result.contourModel}
              </span>
            ) : null}
            <span className={`num ${styles.receiptItem}`}>
              {result.values.length} Maße · {result.runs.length}{" "}
              {result.runs.length === 1 ? "Teilzug" : "Teilzüge"}
            </span>
          </div>

          <OrientationBand orientation={result.orientation} />

          <OutlineReview
            outlines={result.outlines}
            findings={result.outlineFindings}
            drawingUrl={demoPageUrl(result.document.id, 1)}
            onChoose={chooseOutline}
            onConfirm={confirmOutline}
            onRevoke={() => setConfirmedOutlineId(null)}
            confirmedBy={confirmedBy}
            busy={run.isPending}
            superseded={run.isError ? SUPERSEDED_NOTE : null}
            edits={run.variables?.edits}
            onEdits={adjustOutline}
            adjustments={result.adjustments}
            editsClamped={result.editsClamped}
          />

          <div className={styles.split}>
            <div className={styles.sticky}>
              <Panel title="Zeichnung" hint="so, wie sie an das Modell ging" flush>
                <DrawingPane
                  file={result.document}
                  page={marked?.source?.page ?? 1}
                  marked={marked}
                />
              </Panel>
            </div>

            <Panel
              title="Gelesene Maße"
              hint="Wert, Wortlaut, Konfidenz, Fundstelle"
              aside={
                <span className={`label ${styles.markHint}`}>
                  {result.values.some((value) => sourceStateOf(value) === "markable")
                    ? "Wert anklicken markiert die Fundstelle"
                    : "keine belegte Fundstelle in diesem Lauf"}
                </span>
              }
              flush
            >
              <ValueList
                values={result.values}
                placement={placement}
                markedId={markedId}
                onMark={(value) => setMarkedId(value.id === markedId ? null : value.id)}
              />
            </Panel>
          </div>

          <section className={styles.partsSection}>
            <div className={styles.partsHead}>
              <div className={styles.partsTitle}>
                <h2 className={styles.sectionTitle}>Stückliste</h2>
                {/*
                  The parts list is the thing somebody copies from, and the
                  receipt line that dates it sits a screen and a half further
                  up: whoever scrolls here to read the elements has nothing
                  left saying which run produced them. So the origin travels
                  with the artefact — always, not only when it is stale, since
                  "which run was that?" is the same question in both cases.
                  Louder when the result has been superseded, because then it
                  is not an aside but the first thing to know.
                */}
                <p className={styles.partsOrigin}>
                  {stale ? (
                    <>
                      <StatusBadge status="review">Vorheriger Lauf</StatusBadge>
                      <span>
                        {run.isPending
                          ? "Der neue Lauf läuft noch — diese Zahlen stammen aus dem Lauf davor."
                          : "Der neue Lauf ist abgebrochen — diese Zahlen stammen aus dem Lauf davor."}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="label">
                        {result.provider === "replay"
                          ? "Aus dem aufgezeichneten Lauf"
                          : "Aus dem Live-Lauf gegen Mistral"}
                      </span>
                      <span className="num">{result.promptId}</span>
                    </>
                  )}
                </p>
              </div>
              <dl className={styles.grandTotals}>
                <div>
                  <dt className="label">Elemente</dt>
                  <dd className="num">{result.totals.elements}</dd>
                </div>
                <div>
                  <dt className="label">Konsolen</dt>
                  <dd className="num">
                    {/* An upper bound is never printed as a bare number: it is
                        the figure somebody would order against. */}
                    {result.totals.consolesExact ? null : (
                      <span className={styles.bound}>höchstens </span>
                    )}
                    {result.totals.consoles}
                  </dd>
                </div>
                <div>
                  <dt className="label">Teilzüge</dt>
                  <dd className="num">{result.runs.length}</dd>
                </div>
              </dl>
            </div>

            {result.totals.consolesExact ? null : (
              <p className={styles.boundNote}>{result.totals.consolesNote}</p>
            )}
            <PartsList
              runs={result.runs}
              sections={result.partsList}
              grating={result.grating}
            />
            <p className={styles.noPrices}>
              Kein Preis. Die Preislisten liegen noch nicht vor — eine erfundene Zahl wäre hier
              schlimmer als keine.
            </p>
          </section>

          <Panel
            title="Hinweise aus der Prüfung"
            hint="Wortlaut der Prüfschicht, unübersetzt"
            aside={<span className={`num ${styles.issueTotal}`}>{result.issues.length}</span>}
          >
            <IssueList issues={result.issues} />
          </Panel>
        </>
      ) : null}

      {phase === "idle" ? (
        <div className={styles.idle}>
          <StateMessage title="Noch kein Lauf">
            Fall {caseId} ausgewählt — oben lässt sich ein anderer Fall wählen. Der Lauf geht live
            gegen den Anbieter oder zeigt einen aufgezeichneten Lauf und dauert bis zu einer Minute.
          </StateMessage>
        </div>
      ) : null}
    </div>
  );
}
